// SABRINA Types - KBA Asynchroner Nachrichtenaustausch gemäß § 37 Abs. 3 FZV

/**
 * Status einer SABRINA-Nachricht
 */
export enum SabrinaNachrichtStatus {
  ERSTELLT = 'ERSTELLT',
  GESENDET = 'GESENDET',
  EMPFANGEN = 'EMPFANGEN',
  VERARBEITET = 'VERARBEITET',
  FEHLER = 'FEHLER'
}

/**
 * Nachrichtentypen im SABRINA-System
 * 0006 = Posteingang-Anfrage
 * 0007 = Posteingang-Ergebnis
 * 0008 = Nachrichtenabruf
 * 0001 = Sammelnachricht
 * 0002 = Quittung
 */
export enum SabrinaNachrichtTyp {
  POSTEINGANG_ANFRAGE = '0006',
  POSTEINGANG_ERGEBNIS = '0007',
  ABRUF = '0008',
  SAMMELNACHRICHT = '0001',
  QUITTUNG = '0002'
}

/**
 * Richtung der Nachricht (Request/Response)
 */
export enum SabrinaRichtung {
  REQUEST = 'REQUEST',
  RESPONSE = 'RESPONSE'
}

/**
 * Info über eine verfügbare Nachricht im Posteingang (erweitert)
 */
export interface SabrinaNachrichtInfo {
  nachrichtenId: string;
  nachrichtenTyp: string;
  kommunikationsschrittCode?: string;
  kommunikationsschrittName?: string;
  bezugsnachrichtId?: string;
  nachrichtenGroesse?: number;
  zeitpunktErstellung?: string;
  zeitpunktSpeicherung?: string;
  absenderKps?: string;
}

/**
 * Ergebnis der Posteingang-Prüfung (0006 → 0007)
 */
export interface SabrinaPosteingangResult {
  erfolg: boolean;
  anzahlNachrichten: number;
  nachrichten: SabrinaNachrichtInfo[];
  hinweis?: string;
  fehlerCode?: string;
  fehlerMeldung?: string;
}

/**
 * Request für Nachrichtenabruf
 */
export interface SabrinaAbrufRequest {
  nachrichtenIds: string[];
}

/**
 * Eine abgerufene Nachricht (erweitert)
 */
export interface SabrinaAbrufNachricht {
  nachrichtenId: string;
  nachrichtenTyp: string;
  xmlInhalt?: string;
  fin?: string;
  bearbeitungsart?: string;
  gebuehrenGesamt?: number;
  gebuehrenTyp?: string;
  kennzeichenReservierung?: boolean;
  datumWirksamkeit?: string;
  dokumente?: SabrinaDokumentInfo[];
}

/**
 * Dokument-Info aus dem Abruf-Response (enthält Base64-Inhalt)
 */
export interface SabrinaDokumentInfo {
  artDesDokuments: string;
  mimeType: string;
  inhalt?: string;
  dokumentInformation?: string;
}

/**
 * Fehler beim Abruf einer Nachricht
 */
export interface SabrinaAbrufFehler {
  nachrichtenId: string;
  fehlerCode: string;
  fehlerMeldung: string;
}

/**
 * Ergebnis des Nachrichtenabrufs (0008 → 0001)
 */
export interface SabrinaAbrufResult {
  erfolg: boolean;
  anzahlAbgerufen: number;
  nachrichten: SabrinaAbrufNachricht[];
  fehler: SabrinaAbrufFehler[];
}

/**
 * Gespeicherte SABRINA-Nachricht aus der Datenbank
 */
export interface SabrinaNachricht {
  id: number;
  nachrichtenId: string;
  nachrichtenTyp: SabrinaNachrichtTyp | string;
  status: SabrinaNachrichtStatus;
  requestXml?: string;
  responseXml?: string;
  zulassungsantragId?: number;
  fehlerCode?: string;
  fehlerMeldung?: string;
  erstelltAm: string;
  aktualisiertAm?: string;
}

/**
 * Paginierte Liste von SABRINA-Nachrichten
 */
export interface SabrinaNachrichtPage {
  content: SabrinaNachricht[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

// ========== GKS02 – Gebührenabrechnung (XKfz 0103) ==========

export interface Gks02GebuehrenbescheidDokument {
  id: number;
  artDesDokuments?: string;
  mimeType?: string;
  dateiname?: string;
  erstelltAm: string;
}

export interface Gks02Gebuehrenbescheid {
  id: number;
  vollstaendig: boolean;
  sabrinaNachrichtenId?: string;
  nummerGebuehrenbescheid?: string;
  abrechnungszeitraumVon?: string;
  abrechnungszeitraumBis?: string;
  datumFaelligkeit?: string;
  datumEinziehung?: string;
  summeGesamt?: number;
  anzahlAbgerechneteAntraege?: string;
  kassenzeichen?: string;
  verwendungszweck?: string;
  bankverbindungIban?: string;
  bankverbindungBic?: string;
  bankverbindungKontoinhaber?: string;
  bankverbindungEinzugsermaechtigung?: boolean;
  absenderKps?: string;
  bezahlt: boolean;
  bezahltAm?: string;
  erstelltAm: string;
  dokumente: Gks02GebuehrenbescheidDokument[];
}

export type DatevStatus = 'VORGEMERKT' | 'IN_BEARBEITUNG' | 'GESENDET' | 'FEHLER' | 'UNKLAR';

export interface DatevAttempt {
  id: number;
  auftragId: string;
  bescheidId: number;
  bescheidNummer?: string;
  empfaenger: string;
  status: DatevStatus;
  angefordertAm: string;
  gesendetAm?: string;
  fehler?: string;
  erneutBestaetigt: boolean;
  angefordertVon: string;
  anzahlDokumente: number;
}

export interface DatevSummary {
  bescheidId: number;
  wiederholungErforderlich: boolean;
  letzterVersuch: DatevAttempt | null;
  letzterErfolg: DatevAttempt | null;
}

export interface DatevDispatch {
  auftragId: string;
  abgeschlossen: boolean;
  ergebnisse: DatevAttempt[];
}

export interface DatevDispatchRequest {
  requestId: string;
  bescheidIds: number[];
  erneutBestaetigteBescheidIds: number[];
  erwarteterEmpfaenger: string;
}

export interface Gks02AbrufRequest {
  nachrichtenIds: string[];
}

export interface Gks02AbrufResult {
  gespeicherteBescheide: number;
  angeforderteNachrichten: number;
}

// ========== Neue Typen für Backend-Anpassung ==========

/**
 * Eintrag aus der Posteingang-Liste (GET /api/sabrina/posteingang/liste)
 */
export interface SabrinaPosteingang {
  id: number;
  sabrinaNachrichtenId: string;
  kommunikationsschrittCode: string;
  kommunikationsschrittName: string;
  zeitpunktErstellung: string;
  zeitpunktSpeicherung: string;
  absenderKps: string;
  bezugsnachrichtId?: string;
  nachrichtenGroesse?: number;
  erstelltAm: string;
}

/**
 * Gespeichertes SABRINA-Dokument (aus empfangener Nachricht)
 */
export interface SabrinaDokument {
  id: number;
  artDesDokuments: string;
  mimeType: string;
  dateiPfad: string;
  dokumentInformation?: string;
  erstelltAm: string;
}

/**
 * Empfangene und verarbeitete Nachricht (GET /api/sabrina/empfangen)
 */
export interface SabrinaEmpfangeneNachricht {
  id: number;
  sabrinaNachrichtenId: string;
  nachrichtentyp: string;
  kommunikationsschrittCode: string;
  kommunikationsschrittName: string;
  kbaAntragsId?: string;
  fin?: string;
  zulassungsantrag?: { id: number };
  absenderKps?: string;
  geschaeftsvorfallArt?: string;
  antragStatus?: string;
  bearbeitungsart?: string;
  gebuehrenGesamt?: number;
  gebuehrenTyp?: string;
  kennzeichenReservierung?: boolean;
  datumWirksamkeit?: string;
  zeitpunktErstellung?: string;
  ablehnungsgrundCode?: string;
  ablehnungsgrundName?: string;
  prozesshinweis?: string;
  responseXml?: string;
  erstelltAm: string;
  dokumente: SabrinaDokument[];
}
